/* Disabling TS-ESLint is in general bad, but for a complex piece of library
 * code such as this, it's almost unavoidable - I couldn't find a simple way to
 * unroll this bad boy in such a way to make these work */

/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import * as React from "react";
import { proxyWithoutTracking as proxy, ref } from "./valtioReexport";
import { useSnapshot } from "./useSnapshot";

export class StoreWithProps<T extends Record<string, unknown>> {
  public props: T;

  constructor(props: T) {
    // Props are wrapped with ref() to make them non-reactive. This allows
    // useClassState to update props during render without triggering the
    // "Cannot update a component while rendering" warning.
    // Subclasses should access props via this.props and use getters for
    // derived values that depend on props.
    this.props = ref({ ...props }) as T;
  }
}

/**
 * useClassState(ClassConstructor)
 *
 * Returns ONE object that merges
 *   • reactive data fields (snapshot)
 *   • bound methods that mutate via `this`
 *
 * Plain Class Example:
 * class TodoStore {
 *   items = []
 *   complexObject: HTMLElement | File ;
 *
 *   addTodo(text) {
 *     this.items.push({ id: Date.now(), text, done: false })
 *   }
 *
 *  setComplexObject(obj: HTMLElement | File) {
 *    // For complex objects that do not need (or don't support) internal state
 *    // tracking, use the ref() function to mark them as do-not-proxy:
 *    this.complexObject = ref(obj);
 *  }
 *
 *   toggleTodo(id) {
 *     const todo = this.items.find(item => item.id === id)
 *     if (todo) todo.done = !todo.done
 *   }
 * }
 *
 * const todos = useClassState(TodoStore)
 * // Usage: todos.addTodo("Buy milk"), todos.toggleTodo(123)
 *
 *
 * const userStore = useClassState(UserStore, { userId: "123", initialData: userData })
 * // Usage: userStore.name, userStore.updateName("New Name"), userStore.props.userId
 */
// Overload for plain class (no constructor arguments)
export function useClassState<T extends object>(initial: new () => T): Readonly<ValidStructure<T>>;
// Overload for StoreWithProps subclass
export function useClassState<T extends StoreWithProps<P>, P extends Record<string, unknown>>(
  initial: new (props: P) => T,
  props: P,
): Readonly<ValidStructure<T>>;

export function useClassState<T extends object>(initial: new (...args: any[]) => T, props?: any): Readonly<T> {
  // Runtime check to ensure only class constructors are passed
  if (typeof initial !== "function") {
    throw new Error("useClassState only accepts class constructors. Plain objects are not supported.");
  }

  // Holds the bound methods (stable reference for lifetime of component)
  const actionsRef = React.useRef<Set<string | symbol> | undefined>();
  actionsRef.current ??= new Set();

  // Holds the Valtio proxy store
  const storeRef = React.useRef<T>();
  if (!storeRef.current) {
    // -----------------------
    // Create proxy once
    // -----------------------
    const raw = props !== undefined ? new initial(props) : new initial();
    const store = proxy(raw);

    for (const [key, value] of iterateOwnProps(store)) {
      if (typeof value === "function") {
        // @ts-ignore – binding keeps the original type but TS can't infer it
        store[key] = value.bind(store);
        actionsRef.current.add(key);
      }
    }

    storeRef.current = store;
  } else if (props !== undefined && "props" in storeRef.current) {
    // Update props directly during render. This is safe because props is wrapped
    // with ref() in StoreWithProps, making it non-reactive. The component will
    // re-render when the parent passes new props, and getters that read from
    // this.props will return the updated values.
    Object.assign(storeRef.current.props as Record<string, unknown>, props as Record<string, unknown>);
  }

  // Reactive read snapshot (safe to spread/destructure)
  const snap = useSnapshot(storeRef.current);
  let rendering = true;

  React.useLayoutEffect(() => {
    // "turn off" the snapshotting behavior after the render cycle, see useProxy implementation: https://github.com/pmndrs/valtio/blob/main/src/react/utils/useProxy.ts
    // eslint-disable-next-line react-hooks/exhaustive-deps
    rendering = false;
  });

  // istanbul ignore if -- @preserve defensive check
  if (!snap || typeof snap !== "object") throw new Error("Unexpected snap type");

  return new Proxy(snap, {
    get(target, p, receiver) {
      // istanbul ignore if -- @preserve defensive check
      if (!storeRef.current || !actionsRef.current) throw new Error("Invalid useClassState access");

      // If we're not rendering, we should go directly to the store:
      if (!rendering) return Reflect.get(storeRef.current, p, receiver);

      // When accessing actions, they should go to the store:
      if (actionsRef.current.has(p)) return Reflect.get(storeRef.current, p, receiver);

      // otherwise, they go to the snapshot:
      return Reflect.get(target, p, receiver);
    },
    set() {
      throw new Error("useClassState properties are read-only");
    },
  }) as Readonly<T>;
}

/**
 * Iterate own property names across prototype chain (excluding constructor)
 *
 * TODO (maybe?) : Prototype Chain Security - This function walks the prototype chain which could
 * potentially expose unintended properties. While it excludes constructor, it doesn't
 * validate property origins which could lead to unexpected behavior if malicious objects
 * are passed or if prototype pollution occurs. Consider implementing:
 * - Property origin validation (hasOwnProperty checks)
 * - Whitelist of safe prototype levels
 * - Exclusion of dangerous properties (__proto__, etc.)
 * - Prototype pollution detection
 */
function* iterateOwnProps(obj: object) {
  let current: any = obj;
  while (current && current !== Object.prototype) {
    for (const key of Object.getOwnPropertyNames(current)) {
      if (key !== "constructor") {
        yield [key, (obj as any)[key]] as const;
      }
    }
    current = Object.getPrototypeOf(current);
  }
}

type ValidFunction = (...args: any) => undefined | void | Promise<unknown>;

interface ClassTypeError<Error extends string> {
  Error: Error;
}

/** ValidStructure requires that all methods either return void or Promise.
 * This is so that you can't accidentally use "complex methods" during render in order
 * to change what's rendering. Methods are for mutations. Use getters & properties for state access.
 * If you need to do post-processing on the state after access, you can use helper methods outside of
 * the class, or upgrade to a full Valtio-based store/snapshot mechanism.
 */
type ValidStructure<T> = T extends ValidFunction
  ? T
  : T extends (...args: any) => any
    ? ClassTypeError<"Methods on useClassState-s may only return Promises or nothing"> // disallow functions with bad return types
    : T extends (infer U)[]
      ? ValidStructure<U>[] // recurse into arrays
      : T extends object
        ? // Specific callout for branded primitive types
          T extends { __brand: string }
          ? T
          : { [K in keyof T]: ValidStructure<T[K]> } // recurse into nested objects
        : T; // allow primitives
